import type {
    StreamCallbacks,
    StreamOptions,
    ProcessStreamParams,
} from '@/types/stream';
import { redirectToSignIn } from '@/lib/sign-in-redirect';
import { refreshEngineSession } from '@/services/engine/engine-client';

const processStream = async ({
    reader,
    decoder,
    buffer = '',
    chunkIndex = 0,
    onChunk,
    onDone,
    onEmptyResponse,
    onStart,
}: ProcessStreamParams): Promise<void> => {
    if (chunkIndex === 0) onStart();

    const { done, value } = await reader.read();

    if (done) {
        if (chunkIndex === 0) {
            return onEmptyResponse();
        }

        onDone();
        return flushBuffer(buffer, onChunk, chunkIndex);
    }

    const newBuffer = buffer + decoder.decode(value, { stream: true });
    const { lines, partialBuffer } = splitLines(newBuffer);

    lines.forEach((line, index) => {
        onChunk(line.trim(), chunkIndex + index);
    });

    const newIndex = chunkIndex + lines.length;

    return processStream({
        reader,
        decoder,
        buffer: partialBuffer,
        chunkIndex: newIndex,
        onChunk,
        onDone,
        onEmptyResponse,
        onStart,
    });
};

const splitLines = (
    text: string,
): { lines: string[]; partialBuffer: string } => {
    const lines = text.split('\n');
    const partialBuffer = lines.pop() ?? '';
    return { lines, partialBuffer };
};

const flushBuffer = (
    buffer: string,
    onChunk: StreamCallbacks['onChunk'],
    chunkIndex: number,
): void => {
    const trimmed = buffer.trim();
    if (trimmed) {
        onChunk(trimmed, chunkIndex);
    }
};

const extractErrorMessage = async (response: Response): Promise<string> => {
    try {
        const errorData = await response.json();
        return (
            errorData.detail ||
            errorData.message ||
            errorData.error ||
            `Request failed - Status: ${response.status}, ${response.statusText}`
        );
    } catch {
        return `Request failed - Status: ${response.status}, ${response.statusText}`;
    }
};

const requestStream = ({
    method,
    url,
    body,
    signal,
}: StreamOptions): Promise<Response> =>
    fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
        signal,
    });

const tryRefreshSession = async (): Promise<boolean> => {
    try {
        await refreshEngineSession();
        return true;
    } catch {
        return false;
    }
};

const requestStreamWithSessionRefresh = async (
    options: StreamOptions,
): Promise<Response> => {
    const response = await requestStream(options);
    if (response.status !== 401 || !(await tryRefreshSession())) {
        return response;
    }
    return requestStream(options);
};

const fetchStream = async (
    options: StreamOptions,
): Promise<Response | null> => {
    try {
        const response = await requestStreamWithSessionRefresh(options);

        if (!response.ok || !response.body) {
            if (response.status === 401) {
                redirectToSignIn();
                throw new Error(`Unauthorized - Status: ${response.status}`);
            }

            const errorMessage = await extractErrorMessage(response);
            throw new Error(errorMessage);
        }

        return response;
    } catch (error) {
        throw error instanceof Error
            ? error
            : new Error(`Fetch error: ${error}`);
    }
};

const isAbortError = (error: unknown): boolean =>
    error instanceof DOMException && error.name === 'AbortError';

export { processStream, fetchStream, isAbortError };
